/**
 * opReconciler.ts
 *
 * Pure cross-type reconciliation for the sync queue. The SyncWatcher executes
 * op buckets in a fixed TYPE order, which ignores createdAt ordering BETWEEN
 * types. For inverse op pairs on the same block that breaks user intent, so
 * resolve them before processing:
 *
 *   - create→delete in one cycle (block created then deleted before syncing):
 *     cancel BOTH — the block never needs to reach the server. This also
 *     prevents the resurrection case (create fails, idempotent delete no-ops,
 *     retried create restores a page the user deleted).
 *   - delete→create (undo of a delete): the create runs before the delete in
 *     the cycle, so a stale delete would re-kill the restored block — drop it.
 *   - delete→create→delete on a PRE-EXISTING block: the block exists on the
 *     server, so the final delete MUST still be sent (don't cancel it, or the
 *     server keeps a ghost).
 *   - trash↔restore: the backend endpoints are NOT idempotent (trash throws
 *     unless alive, restore throws unless deleted). A sequence whose first and
 *     last op types differ nets out to the server's current state → send
 *     nothing (sending would 400 and dead-letter). Same type first & last →
 *     real state change → send only the latest.
 *
 * Pure function — no IDB access — so it is directly unit-testable. The caller
 * (SyncWatcher) marks cancelled ops synced and cancels their retry timers.
 */

import type { SyncQueueRecord, BlockDeletePayload } from "../db/schema";

export interface InverseOpReconciliation {
  /** Ops superseded by a later inverse op — mark synced, never send. */
  cancelledOpIds: Set<string>;
  /** Blocks whose pending create was cancelled (created + deleted locally). */
  cancelledCreateBlockIds: Set<string>;
}

export function reconcileInverseOps(
  blockCreateOps: SyncQueueRecord[],
  blockDeleteOps: SyncQueueRecord[],
  blockTrashOps: SyncQueueRecord[],
  blockRestoreOps: SyncQueueRecord[],
): InverseOpReconciliation {
  const cancelledOpIds = new Set<string>();
  const cancelledCreateBlockIds = new Set<string>();

  // CREATE/DELETE inverse pairs on the same block.
  // op.noteId on a BLOCK_CREATE is the created block's _id (see createBlocksMutation).
  const latestCreateByBlock = new Map<string, SyncQueueRecord>();
  for (const op of blockCreateOps) {
    if (!op.noteId) continue;
    const cur = latestCreateByBlock.get(op.noteId);
    if (!cur || op.createdAt > cur.createdAt) latestCreateByBlock.set(op.noteId, op);
  }

  const deletesByBlock = new Map<string, SyncQueueRecord[]>();
  for (const op of blockDeleteOps) {
    const bId = (op.payload as BlockDeletePayload).blockId;
    if (!bId) continue;
    const list = deletesByBlock.get(bId);
    if (list) list.push(op);
    else deletesByBlock.set(bId, [op]);
  }

  // Evidence that a block existed on the server BEFORE this cycle's create:
  // any pending lifecycle op (delete/trash/restore) created EARLIER than the
  // create. In that case the create is an undo-restore of a real server block,
  // so a final delete must still be SENT.
  const preExisted = (blockId: string, before: number): boolean => {
    const earlier = (op: SyncQueueRecord) =>
      (op.payload as BlockDeletePayload).blockId === blockId && op.createdAt < before;
    return (
      (deletesByBlock.get(blockId) ?? []).some((op) => op.createdAt < before) ||
      blockTrashOps.some(earlier) ||
      blockRestoreOps.some(earlier)
    );
  };

  for (const [blockId, dels] of deletesByBlock) {
    const createOp = latestCreateByBlock.get(blockId);
    if (!createOp) continue;
    const latestDel = dels.reduce((a, b) =>
      b.createdAt > a.createdAt || (b.createdAt === a.createdAt && b.id > a.id) ? b : a,
    );
    if (latestDel.createdAt >= createOp.createdAt) {
      // Final intent: deleted. The pending create + any stale earlier deletes
      // never need to reach the server.
      cancelledOpIds.add(createOp.id);
      for (const d of dels) {
        if (d !== latestDel) cancelledOpIds.add(d.id);
      }
      if (preExisted(blockId, createOp.createdAt)) {
        // delete→create→delete: block pre-existed server-side; keep the final
        // delete so the server actually removes it.
      } else {
        // Brand-new this cycle: never reached the server. Drop the delete too
        // (also prevents the create-fails-then-resurrects case).
        cancelledOpIds.add(latestDel.id);
        cancelledCreateBlockIds.add(blockId);
      }
    } else {
      // All deletes precede the create (undo of a delete): drop the stale
      // deletes, keep the create.
      for (const d of dels) cancelledOpIds.add(d.id);
    }
  }

  // TRASH/RESTORE — net-out. The server status is implied by the FIRST pending
  // op for the block (a pending trash means the server still has it alive, etc.).
  const trashRestoreByBlock = new Map<string, SyncQueueRecord[]>();
  for (const op of [...blockTrashOps, ...blockRestoreOps]) {
    if (cancelledOpIds.has(op.id)) continue;
    const bId = (op.payload as BlockDeletePayload).blockId;
    if (!bId) continue;
    // Ops on a block whose create was cancelled are moot (block never existed).
    if (cancelledCreateBlockIds.has(bId)) {
      cancelledOpIds.add(op.id);
      continue;
    }
    const list = trashRestoreByBlock.get(bId);
    if (list) list.push(op);
    else trashRestoreByBlock.set(bId, [op]);
  }
  for (const ops of trashRestoreByBlock.values()) {
    if (ops.length < 2) continue; // a single trash or restore is left as-is
    const sorted = [...ops].sort(
      (a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );
    const first = sorted[0]!;
    const latest = sorted[sorted.length - 1]!;
    if (first.operationType !== latest.operationType) {
      // Nets out to the server's current state → send nothing.
      for (const op of sorted) cancelledOpIds.add(op.id);
    } else {
      // Real state change → send only the latest.
      for (const op of sorted) {
        if (op !== latest) cancelledOpIds.add(op.id);
      }
    }
  }

  return { cancelledOpIds, cancelledCreateBlockIds };
}
